import { waitUntilOpen } from "./utils/spawnTask.js";

const SELECTOR_MAP = new Map();
let MUTATION_OBSERVER = null;
let MUTATION_TIMER;

function getMutationObserver() {
    if (MUTATION_OBSERVER === null) {
        MUTATION_OBSERVER = new MutationObserver(onMutationCallback);
    }

    return MUTATION_OBSERVER;
}

function removeState(selector, state) {
    const states = SELECTOR_MAP.get(selector);
    if (!states) {
        return;
    }

    states.delete(state);

    if (states.size === 0) {
        SELECTOR_MAP.delete(selector);
    }

    if (SELECTOR_MAP.size === 0) {
        getMutationObserver().disconnect();
    }
}

function runSafeCallback(callback, node) {
    function execute() {
        callback(node);
    }

    waitUntilOpen().then(execute).catch(console.error);
}

function propagateNodeToSelector(selector, node) {
    function propagator(state, _state, _set) {
        const { oneTime, initializedNodes, callback, target } = state;

        if (target && !target.contains(node)) {
            return;
        }

        if (initializedNodes.has(node)) {
            return;
        }

        runSafeCallback(callback, node);

        if (oneTime) {
            removeState(selector, state);
            return;
        }

        initializedNodes.add(node);
    }

    return propagator;
}

function onEachSelector(states, selector, _map) {
    function handleNode(node) {
        states.forEach(propagateNodeToSelector(selector, node));
    }

    const nodes = document.querySelectorAll(selector);
    nodes.forEach(handleNode);
}

function onMutationCallback() {
    if (MUTATION_TIMER) {
        clearTimeout(MUTATION_TIMER);
    }

    function onceUpdatesAreDone() {
        MUTATION_TIMER = null;
        clearTimeout(MUTATION_TIMER);
        SELECTOR_MAP.forEach(onEachSelector);
    }

    MUTATION_TIMER = setTimeout(onceUpdatesAreDone, 2);
}

function addToObserver(selector, state) {
    if (!SELECTOR_MAP.has(selector)) {
        SELECTOR_MAP.set(selector, new Set());
    }

    const states = SELECTOR_MAP.get(selector);
    states.add(state);

    const mutationObserver = getMutationObserver();

    if (SELECTOR_MAP.size === 1) {
        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
}

export default function createElementChangeListener(selector, callback, oneTime, target) {
    const initializedNodes = new Set([]);

    const state = {
        oneTime,
        initializedNodes,
        callback,
        target,
    };

    let reported = false;

    const root = target || document;

    root.querySelectorAll(selector).forEach((node) => {
        if (oneTime && reported) {
            return;
        }
        reported = true;
        runSafeCallback(callback, node);
        initializedNodes.add(node);
    });

    function disconnect() {
        removeState(selector, state);
    }

    if (!oneTime || !reported) {
        addToObserver(selector, state);
    }

    return {
        disconnect,
        observe() {
            throw new Error("Unallowed use of internal mutation observer");
        },
        takeRecords() {
            return getMutationObserver().takeRecords();
        },
    };
}
